import { Compass, HelpCircle, Keyboard, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const NAV_SHORTCUTS: Array<{ keys: string; label: string; path: string }> = [
  { keys: "g o", label: "Overview", path: "/admin" },
  { keys: "g s", label: "Orders & sales", path: "/admin/analytics" },
  { keys: "g p", label: "Payment inbox", path: "/admin/payments" },
  { keys: "g h", label: "Help inbox", path: "/admin/help-messages" },
  { keys: "g v", label: "Reviews", path: "/admin/reviews" },
  { keys: "g q", label: "Production queue", path: "/admin/queue" },
  { keys: "g r", label: "Products", path: "/admin/products" },
  { keys: "g m", label: "Payment methods", path: "/admin/payment-methods" },
  { keys: "g w", label: "Website content", path: "/admin/settings" },
  { keys: "g a", label: "My account", path: "/admin/account" }
];

const GUIDE_STEPS = [
  { title: "1. Review payments", body: "New payment proofs land in Payment inbox. Approve to send the order to production, or request a clearer proof." },
  { title: "2. Track production", body: "Approved orders appear in Production queue automatically. Move each order through the stages as the work happens." },
  { title: "3. Manage products", body: "Add or edit what's for sale, prices, photos, and stock in Products." },
  { title: "4. Watch the numbers", body: "Orders & sales shows revenue, top products, and the full order history — including cancelled and rejected orders." },
  { title: "5. Stay reachable", body: "Questions customers send through the site's help widget land in Help inbox." },
  { title: "6. Keep content fresh", body: "FAQs, policies, and contact info shown to customers live in Website content." },
  { title: "7. Curate reviews", body: "Customer star ratings wait in Reviews until you publish them. Homepage quote cards are managed separately in Website content." }
];

const SEEN_KEY = "minis-admin-help-seen";

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable;
}

export function AdminHelpWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"guide" | "shortcuts">("guide");
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsFirstVisit(!localStorage.getItem(SEEN_KEY));
  }, []);

  useEffect(() => {
    let awaitingSecondKey = false;
    let timer = 0;
    function handleKeyDown(event: KeyboardEvent) {
      if (isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "?") {
        event.preventDefault();
        setOpen((current) => !current);
        localStorage.setItem(SEEN_KEY, "true");
        setIsFirstVisit(false);
        return;
      }
      if (awaitingSecondKey) {
        awaitingSecondKey = false;
        window.clearTimeout(timer);
        const match = NAV_SHORTCUTS.find((shortcut) => shortcut.keys === `g ${event.key.toLowerCase()}`);
        if (match) navigate(match.path);
        return;
      }
      if (event.key.toLowerCase() === "g") {
        awaitingSecondKey = true;
        timer = window.setTimeout(() => { awaitingSecondKey = false; }, 1500) as unknown as number;
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => { window.removeEventListener("keydown", handleKeyDown); window.clearTimeout(timer); };
  }, [navigate]);

  const toggle = () => {
    setOpen((current) => !current);
    localStorage.setItem(SEEN_KEY, "true");
    setIsFirstVisit(false);
  };

  return <>
    <button
      onClick={toggle}
      aria-label={open ? "Close help" : "Open help and shortcuts"}
      className="fixed bottom-5 right-5 z-50 grid size-14 place-items-center rounded-full bg-ink text-cream shadow-sticker transition hover:scale-105 sm:bottom-7 sm:right-7"
    >
      {open ? <X size={24} /> : <HelpCircle size={24} />}
      {isFirstVisit && !open && <span className="absolute right-0 top-0 size-3.5 animate-pulse rounded-full bg-clay ring-2 ring-cream" />}
    </button>
    {open && <div className="fixed inset-x-4 bottom-24 z-50 mx-auto flex max-h-[70vh] w-auto max-w-sm flex-col overflow-hidden rounded-[1.75rem] border border-ink/10 bg-white shadow-soft sm:inset-x-auto sm:right-7">
      <div className="border-b border-ink/10 bg-paper/60 px-5 py-4">
        <p className="font-display text-lg font-bold text-clay-dark">Studio help</p>
        <p className="text-xs text-ink/50">A quick tour, plus keyboard shortcuts.</p>
        <div className="mt-3 flex gap-2">
          <button onClick={() => setTab("guide")} className={`rounded-full px-3 py-1.5 text-xs font-extrabold ${tab === "guide" ? "bg-ink text-white" : "border border-ink/10 bg-white text-ink/60"}`}><Compass size={13} className="mr-1 inline" /> Getting started</button>
          <button onClick={() => setTab("shortcuts")} className={`rounded-full px-3 py-1.5 text-xs font-extrabold ${tab === "shortcuts" ? "bg-ink text-white" : "border border-ink/10 bg-white text-ink/60"}`}><Keyboard size={13} className="mr-1 inline" /> Shortcuts</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {tab === "guide" && <div className="grid gap-3">
          {GUIDE_STEPS.map((step) => <div key={step.title} className="rounded-2xl bg-paper/60 p-4">
            <p className="text-sm font-extrabold text-clay-dark">{step.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink/65">{step.body}</p>
          </div>)}
        </div>}
        {tab === "shortcuts" && <div className="grid gap-2">
          <div className="flex items-center justify-between rounded-xl bg-paper/60 px-3 py-2 text-sm"><span className="font-bold">Open this help panel</span><kbd className="rounded-md border border-ink/15 bg-white px-2 py-0.5 font-mono text-xs font-bold">?</kbd></div>
          {NAV_SHORTCUTS.map((shortcut) => <button key={shortcut.keys} onClick={() => { navigate(shortcut.path); setOpen(false); }} className="flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm hover:bg-paper/60">
            <span className="font-bold">{shortcut.label}</span>
            <span className="flex gap-1">{shortcut.keys.split(" ").map((key, index) => <kbd key={index} className="rounded-md border border-ink/15 bg-white px-2 py-0.5 font-mono text-xs font-bold">{key}</kbd>)}</span>
          </button>)}
          <p className="mt-1 px-3 text-xs text-ink/40">Press <kbd className="rounded border border-ink/15 bg-white px-1">g</kbd> then a letter. Disabled while typing in a field.</p>
        </div>}
      </div>
    </div>}
  </>;
}
